import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, User as UserIcon, Calendar, KeyRound, Save } from "lucide-react";
import { toast } from "sonner";
import { fmtDate as fmtDateLocal } from "@/lib/formatDate";

export default function Profile() {
    const { user, refresh } = useAuth();
    const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
    const [profileSaving, setProfileSaving] = useState(false);
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [pwdSaving, setPwdSaving] = useState(false);

    if (!user) return null;

    const initials = (user.name || user.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    const fmtDate = (d) => fmtDateLocal(d);
    const dirty = profile.name !== (user.name || "") || profile.email !== (user.email || "");

    const saveProfile = async (e) => {
        e.preventDefault();
        if (!profile.name.trim() || !profile.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        setProfileSaving(true);
        try {
            await api.patch("/auth/me", { name: profile.name, email: profile.email });
            await refresh();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
        } finally {
            setProfileSaving(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (!pwd.current || !pwd.next) { toast.error("Enter your current and new password"); return; }
        if (pwd.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
        if (pwd.next !== pwd.confirm) { toast.error("New passwords don't match"); return; }
        setPwdSaving(true);
        try {
            await api.patch("/auth/me", { current_password: pwd.current, new_password: pwd.next });
            toast.success("Password updated");
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Password update failed");
        } finally {
            setPwdSaving(false);
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-3xl space-y-6 animate-fade-in" data-testid="profile-page">
            <div>
                <h1 className="ph-h1">Your profile</h1>
                <p className="text-sm text-ph-muted mt-1">Update your account info and password.</p>
            </div>

            {/* Account summary */}
            <section className="ph-card p-5">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-ph-primarySoft text-ph-primary flex items-center justify-center text-lg font-semibold">
                        {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-base font-semibold text-ph-text">{user.name}</div>
                        <div className="text-sm text-ph-muted truncate">{user.email}</div>
                        <div className="flex items-center gap-3 mt-1.5">
                            {user.role === "admin" ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium">
                                    <Shield className="w-3 h-3" /> Admin
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">
                                    <UserIcon className="w-3 h-3" /> Member
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-[11px] text-ph-mutedSoft">
                                <Calendar className="w-3 h-3" /> Joined {fmtDate(user.created_at)}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Profile form */}
            <section className="ph-card p-5">
                <h2 className="ph-h2 mb-1">Account details</h2>
                <p className="text-xs text-ph-muted mb-4">Update your name and email.</p>
                <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Full name</label>
                            <Input
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                data-testid="profile-name-input"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Email</label>
                            <Input
                                type="email"
                                value={profile.email}
                                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                data-testid="profile-email-input"
                                required
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={!dirty || profileSaving}
                            className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="profile-save-btn"
                        >
                            <Save className="w-4 h-4 mr-1.5" /> {profileSaving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </section>

            {/* Password */}
            <section className="ph-card p-5">
                <div className="flex items-center gap-2 mb-1">
                    <KeyRound className="w-4 h-4 text-ph-primary" />
                    <h2 className="ph-h2">Password</h2>
                </div>
                <p className="text-xs text-ph-muted mb-4">Change your password. You'll stay signed in on this device.</p>
                <form onSubmit={changePassword} className="space-y-4" data-testid="password-form">
                    <div>
                        <label className="text-xs font-medium text-ph-text mb-1.5 block">Current password</label>
                        <Input
                            type="password"
                            value={pwd.current}
                            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                            data-testid="password-current-input"
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">New password</label>
                            <Input
                                type="password"
                                minLength={6}
                                value={pwd.next}
                                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                                data-testid="password-new-input"
                                autoComplete="new-password"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Confirm new password</label>
                            <Input
                                type="password"
                                minLength={6}
                                value={pwd.confirm}
                                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                                data-testid="password-confirm-input"
                                autoComplete="new-password"
                                required
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={pwdSaving}
                            className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="password-save-btn"
                        >
                            <KeyRound className="w-4 h-4 mr-1.5" /> {pwdSaving ? "Updating…" : "Update password"}
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
}
